import { contact } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { AnimatedText, EVENTS, Grid, SmallButton } from "@folio-2026/shared"
import { useGSAP } from "@gsap/react"
import { gsap, ScrollTrigger } from "gsap/all"
import { useRef } from "react"

import LinkedInLogoSvg from "../../assets/linkedin_logo.svg?react"
import XLogoSvg from "../../assets/x_logo.svg?react"

gsap.registerPlugin(ScrollTrigger)

export default function Contact() {
  const triggerRef = useRef<HTMLDivElement | null>(null)

  const experience = useRef(Experience.getInstance())

  useGSAP(
    () => {
      if (!triggerRef.current) {
        console.warn("Contact: triggerRef.current is null")
        return
      }

      let hasEntered = false

      ScrollTrigger.create({
        trigger: triggerRef.current,
        start: "top bottom",
        end: "top top",
        markers: false,
        scrub: true,
        onEnter() {
          experience.current.trigger(EVENTS.Contact.BeforeEnter)
        },
        onUpdate({ progress }) {
          experience.current.trigger(EVENTS.Contact.Entering, [
            {
              progress,
            },
          ])

          if (progress >= 0.95 && !hasEntered) {
            hasEntered = true
            experience.current.trigger(EVENTS.Contact.Entered)
          } else if (progress < 0.95) {
            hasEntered = false
          }
        },
        onLeaveBack() {
          experience.current.trigger(EVENTS.Contact.Left)
          hasEntered = false
        },
      })
    },
    {
      scope: triggerRef,
    },
  )

  return (
    <>
      <div
        ref={triggerRef}
        className="relative h-screen flex flex-col justify-center items-stretch"
      >
        <Grid>
          <div className="col-start-1 md:col-start-4 lg:col-start-7 col-span-4 md:col-span-4 text-gray-500">
            <AnimatedText
              preventUpdate
              className="text-gray-700"
              data-font-bold
              content="[[[[]"
              tag="h1"
            />
            <AnimatedText
              preventUpdate
              data-font-bold
              className="text-gray-700"
              content={`     [[ ${contact.title1}]`}
              tag="h1"
              highlight={{
                substring: contact.title1,
                className: "text-gray-200",
              }}
            />
            <AnimatedText
              preventUpdate
              className="text-gray-700"
              data-font-bold
              content="        []]"
              tag="h1"
            />
          </div>
          <div className="col-start-1 col-span-4 md:col-start-5 lg:col-start-8 md:col-span-4 text-gray-500">
            <div className="flex gap-2">
              <SmallButton
                preventUpdate
                id="contact-email-cta"
                className="mt-1 2xl:mt-4 w-15"
                hoverEvent={EVENTS.Contact.Hover}
                outEvent={EVENTS.Contact.Out}
                clickEvent={EVENTS.Contact.EmailCTA.Click}
              >
                E-MAIL
              </SmallButton>
              <SmallButton
                preventUpdate
                id="contact-telegram-cta"
                className="mt-1 2xl:mt-4 w-15"
                hoverEvent={EVENTS.Contact.Hover}
                outEvent={EVENTS.Contact.Out}
                clickEvent={EVENTS.Contact.TelegramCTA.Click}
              >
                TELEGRAM
              </SmallButton>
            </div>
          </div>

          <div className="col-start-1 col-span-4 md:col-start-5 lg:col-start-8 md:col-span-4 text-gray-500">
            <div className="flex gap-2">
              <SmallButton
                preventUpdate
                id="contact-x-cta"
                className="mt-1 w-6"
                hoverEvent={EVENTS.Contact.Hover}
                outEvent={EVENTS.Contact.Out}
                clickEvent={EVENTS.Contact.XCTA.Click}
                icon
                iconSize={24}
              >
                <XLogoSvg />
              </SmallButton>
              <SmallButton
                icon
                iconSize={24}
                preventUpdate
                id="contact-linkedin-cta"
                className="mt-1 w-6"
                hoverEvent={EVENTS.Contact.Hover}
                outEvent={EVENTS.Contact.Out}
                clickEvent={EVENTS.Contact.LinkedInCTA.Click}
              >
                <LinkedInLogoSvg />
              </SmallButton>
            </div>
          </div>
        </Grid>
        <Grid className="hidden md:grid">
          <div className="col-start-1 md:col-start-4 lg:col-start-7 col-span-4 lg:col-span-2 md:col-span-4 text-gray-500">
            <AnimatedText
              preventUpdate
              className="text-gray-700 mt-5"
              data-font-bold
              content="]]]"
              tag="h1"
            />
            <AnimatedText
              preventUpdate
              className="text-gray-700"
              data-font-bold
              content="                    [[[]"
              tag="h1"
            />
            <AnimatedText
              preventUpdate
              data-font-bold
              className="text-gray-700"
              content={`      [[ ${contact.title2}]`}
              tag="h1"
              highlight={{
                substring: contact.title2,
                className: "text-gray-200",
              }}
            />
          </div>
        </Grid>
        <Grid className="hidden md:grid">
          <div className="col-start-1 col-span-4 md:col-start-5 lg:col-start-8 md:col-span-4 lg:col-span-5 2xl:col-span-4 2xl:col-start-8 text-gray-500">
            {contact.credits.map((credit) => (
              <div key={credit.name}>
                <AnimatedText
                  preventUpdate
                  className="text-gray-400 mt-3"
                  content={credit.text}
                />
              </div>
            ))}
          </div>
        </Grid>
      </div>

      <div className="fixed pb-1 md:pb-4 md:bottom-8 flex h-screen w-full flex-col items-center justify-end text-center">
        <SmallButton
          preventUpdate
          id="contact-linkedin-cta"
          className="w-20"
          hoverEvent={EVENTS.Contact.Hover}
          outEvent={EVENTS.Contact.Out}
          clickEvent={EVENTS.Contact.RestartCTA.Click}
        >
          RESTART
        </SmallButton>
      </div>
    </>
  )
}
